import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useSharedValue,
  withRepeat,
  withTiming,
  Easing,
  useAnimatedReaction,
  runOnJS,
  cancelAnimation,
} from 'react-native-reanimated';
import { STUCKED_PROGRESS_BAR_TIMEOUT } from './ProgressBarBackground.constants';
import { ProgressBarBackgroundProps } from './ProgressBarBackground.types';
import { Brand } from 'src/theme-v2/colors';

export const useProgressBarBackgroundLogic = (props: ProgressBarBackgroundProps) => {
  const { progressColors = [Brand.iris.Iris600, Brand.water.Water500] } = props;
  const colors = useMemo(
    () => [...progressColors, ...progressColors.slice().reverse(), ...progressColors],
    [progressColors],
  );

  const KEY_FRAMES_ANIMATION = useMemo(
    () => [
      { locations: [0.001, 0.001, 0.001, 0.001, 0.001, 1] },
      { locations: [0.001, 0.001, 0.001, 0.001, 0.5, 1] },
      { locations: [0.001, 0.001, 0.001, 0.5, 1, 1] },
      { locations: [0.001, 0.001, 0.5, 1, 1, 1] },
      { locations: [0.001, 0.5, 1, 1, 1, 1] },
      { locations: [0.001, 1, 1, 1, 1, 1] },
    ],
    [progressColors],
  );

  const animationProgressSV = useAnimationProgressBar(props, KEY_FRAMES_ANIMATION);

  // make keyframes accessible on the UI thread
  const framesRef = useRef(KEY_FRAMES_ANIMATION);
  useEffect(() => {
    framesRef.current = KEY_FRAMES_ANIMATION;
  }, [KEY_FRAMES_ANIMATION]);

  const [animatedProps, setAnimatedProps] = useState<{
    locations: number[];
  }>({
    locations: KEY_FRAMES_ANIMATION[0].locations,
  });

  useAnimatedReaction(
    () => animationProgressSV.value,
    value => {
      'worklet';
      const frames = framesRef.current;
      const total = frames.length;

      const fromIndex = Math.floor(value) % total;
      const toIndex = (fromIndex + 1) % total;

      let locations: number[] = [];
      if (fromIndex > toIndex) {
        locations = KEY_FRAMES_ANIMATION[0].locations;
      } else {
        const t = value % 1;
        const from = frames[fromIndex];
        const to = frames[toIndex];
        locations = from.locations.map((location, index) => {
          return location + (to.locations[index] - location) * t;
        });
      }
      runOnJS(setAnimatedProps)({
        locations,
      });
    },
  );

  return { ...animatedProps, colors };
};

const useAnimationProgressBar = (
  props: ProgressBarBackgroundProps,
  KEY_FRAMES_ANIMATION: { locations: number[] }[],
) => {
  const animationProgressSV = useSharedValue(0); // 0 to KEY_FRAMES_ANIMATION.length
  const isPlaying = useRef(false);
  const isProgressBarPlaying = useProgressBarStatus(props);

  const play = useCallback(() => {
    if (!isPlaying.current) {
      isPlaying.current = true;

      const remainingDuration = (KEY_FRAMES_ANIMATION.length - animationProgressSV.value) * 1000; // 1s total

      // Animate to end first, then loop fully
      animationProgressSV.value = withTiming(KEY_FRAMES_ANIMATION.length, { duration: remainingDuration }, finished => {
        if (finished) {
          animationProgressSV.value = 0;
          animationProgressSV.value = withRepeat(
            withTiming(KEY_FRAMES_ANIMATION.length, {
              duration: KEY_FRAMES_ANIMATION.length * 1000,
              easing: Easing.linear,
            }),
            -1,
            false,
          );
        }
      });
    }
  }, []);

  const pause = useCallback(() => {
    isPlaying.current = false;
    cancelAnimation(animationProgressSV); // stays at current progress
  }, []);

  useEffect(() => {
    if (isProgressBarPlaying) {
      play();
    } else {
      pause();
    }
  }, [isProgressBarPlaying]);

  return animationProgressSV;
};

const useProgressBarStatus = ({
  progress,
  stuckedProgressBarTimeout = STUCKED_PROGRESS_BAR_TIMEOUT,
}: ProgressBarBackgroundProps) => {
  const [isProgressBarPlaying, setIsProgressBarPlaying] = useState(stuckedProgressBarTimeout === 0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (stuckedProgressBarTimeout > 0) {
      if (progress < 100) {
        setIsProgressBarPlaying(false);

        timeoutRef.current = setTimeout(() => {
          setIsProgressBarPlaying(true);
        }, stuckedProgressBarTimeout);
      } else if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [progress]);

  return isProgressBarPlaying;
};
